import { useCallback, useEffect, useMemo, useState } from 'react'

import { CoordinateFragment } from '../../../types/graphql'
import ArcGisMap from '../../components/shared/ArcGisMap'
import { MapShape, PopupTemplateAction } from '../../components/shared/ArcGisMap/types'

export type MapShapeAction<T extends { id: number }> = Omit<PopupTemplateAction, 'onClick'> & {
  onShapeActionClick: (datum: T) => void
}

type Props<T extends { id: number }> = {
  data: T[]
  getDescription: (data: T[]) => string | null | undefined
  getCoordinates: (datum: T) => CoordinateFragment[] | null | undefined
  getId?: (datum: T) => string | number // If the ID should be derived from the data, provide a function to do so
  mapShapeActions?: MapShapeAction<T>[]
  getTitle: (datum: T) => string
  isLoading?: boolean
}

export const DataMap = <T extends { id: number }>({
  data,
  getDescription,
  getCoordinates,
  getId: getIdProp,
  mapShapeActions,
  getTitle,
  isLoading,
}: Props<T>) => {
  const getId = useCallback(
    (datum: T) => {
      if (!getIdProp) {
        return datum.id
      }
      return getIdProp(datum)
    },
    [getIdProp]
  )

  const dataById = useMemo(() => {
    const deduped = new Map<string | number, T[]>()
    for (const datum of data ?? []) {
      const id = getId(datum)
      if (!deduped.has(id)) {
        deduped.set(id, [])
      }
      deduped.get(id)?.push(datum)
    }
    return deduped
  }, [data, getId])

  const shapes: MapShape[] = useMemo(() => {
    const addedShapes: MapShape[] = []
    for (const [id, dataForId] of dataById.entries()) {
      const datum = dataForId[0]
      if (!datum) {
        continue
      }

      const coordinates = getCoordinates(datum)
      if (!coordinates?.length) {
        continue
      }

      const actions = mapShapeActions?.map((action) => ({
        icon: action.icon,
        key: action.key,
        onClick: () => action.onShapeActionClick(datum),
        title: action.title,
      }))

      addedShapes.push({
        description: getDescription(dataForId) ?? undefined,
        actions,
        shape: {
          coordinates,
          type: 'coordinates',
        },
        shapeKey: `shape-${id}`,
        showPin: true,
        title: getTitle(datum),
      })
    }

    return addedShapes
  }, [dataById, getCoordinates, mapShapeActions, getDescription, getTitle])

  const [shapesToDisplay, setShapesToDisplay] = useState(shapes)
  useEffect(() => {
    if (isLoading) {
      return // Show the previously loaded map until the new query is complete, otherwise it's a long period of showing a blank map
    }
    setShapesToDisplay(shapes)
  }, [isLoading, shapes])

  if (!shapesToDisplay?.length) {
    return null
  }

  return <ArcGisMap shapes={shapesToDisplay} isLoading={isLoading} />
}
